/** UTF-8 replacement decoding for runtimes without TextDecoder.
 *  https://encoding.spec.whatwg.org/#utf-8-decoder
 */
export class Utf8Decoder {
    private codePoint = 0;
    private bytesNeeded = 0;
    private bytesSeen = 0;
    private lowerBoundary = 0x80;
    private upperBoundary = 0xbf;
    private bomSeen = false;
    private streaming = false;

    private resetSequence(): void {
        this.codePoint = 0;
        this.bytesNeeded = 0;
        this.bytesSeen = 0;
        this.lowerBoundary = 0x80;
        this.upperBoundary = 0xbf;
    }

    decode(input: Uint8Array = new Uint8Array(), options: { stream?: boolean } = {}): string {
        if (!this.streaming) {
            this.resetSequence();
            this.bomSeen = false;
        }
        this.streaming = options.stream === true;
        let output = '';
        const emit = (codePoint: number): void => {
            if (!this.bomSeen) {
                this.bomSeen = true;
                if (codePoint === 0xfeff) return;
            }
            output += String.fromCodePoint(codePoint);
        };

        for (let i = 0; i < input.length; i++) {
            const byte = input[i];
            if (this.bytesNeeded === 0) {
                if (byte <= 0x7f) {
                    emit(byte);
                } else if (byte >= 0xc2 && byte <= 0xdf) {
                    this.bytesNeeded = 1;
                    this.codePoint = byte & 0x1f;
                } else if (byte >= 0xe0 && byte <= 0xef) {
                    this.bytesNeeded = 2;
                    this.codePoint = byte & 0x0f;
                    if (byte === 0xe0) this.lowerBoundary = 0xa0;
                    if (byte === 0xed) this.upperBoundary = 0x9f;
                } else if (byte >= 0xf0 && byte <= 0xf4) {
                    this.bytesNeeded = 3;
                    this.codePoint = byte & 0x07;
                    if (byte === 0xf0) this.lowerBoundary = 0x90;
                    if (byte === 0xf4) this.upperBoundary = 0x8f;
                } else {
                    emit(0xfffd);
                }
                continue;
            }

            if (byte < this.lowerBoundary || byte > this.upperBoundary) {
                this.resetSequence();
                emit(0xfffd);
                // The offending byte may start the next character.
                i--;
                continue;
            }
            this.lowerBoundary = 0x80;
            this.upperBoundary = 0xbf;
            this.codePoint = (this.codePoint << 6) | (byte & 0x3f);
            this.bytesSeen++;
            if (this.bytesSeen === this.bytesNeeded) {
                emit(this.codePoint);
                this.resetSequence();
            }
        }

        if (!this.streaming && this.bytesNeeded > 0) {
            emit(0xfffd);
            this.resetSequence();
        }
        return output;
    }
}
